/**
 * Firestore access for `skidaCrmSettings` — the admin-editable pipeline
 * definition and the bilingual email playbook.
 *
 * Two documents: `pipeline` and `templates`. Both are read by everyone and
 * written only by CRM admins (enforced in firestore.rules.crm.snippet).
 */
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS, SETTINGS_DOCS } from '@/shared/firebase/collections'
import type {
  EmailTemplate,
  PipelineSettings,
  TemplateKey,
  TemplateSettings,
} from '@/types/crm'
import { EMPTY_COMPANY, type BoardMember, type CompanyDetails } from '@/types/company'
import { DEFAULT_OUTCOMES, DEFAULT_STAGES, DEFAULT_TEMPLATES } from '../defaults'

/**
 * Live pipeline settings. Falls back to the built-in defaults when the document
 * does not exist yet, so a fresh project renders correctly before `npm run seed`.
 */
export function subscribeToPipeline(
  onData: (settings: PipelineSettings) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, CRM_COLLECTIONS.settings, SETTINGS_DOCS.pipeline),
    (snap) => {
      const d = snap.data()
      onData({
        stages: Array.isArray(d?.stages) && d.stages.length > 0 ? d.stages : DEFAULT_STAGES,
        outcomes:
          Array.isArray(d?.outcomes) && d.outcomes.length === 2 ? d.outcomes : DEFAULT_OUTCOMES,
        updatedAt: d?.updatedAt,
        updatedBy: d?.updatedBy,
      })
    },
    (err) => onError(err as Error),
  )
}

export async function savePipeline(
  settings: Pick<PipelineSettings, 'stages' | 'outcomes'>,
  updatedBy: string,
): Promise<void> {
  await setDoc(
    doc(db, CRM_COLLECTIONS.settings, SETTINGS_DOCS.pipeline),
    { ...settings, updatedBy, updatedAt: serverTimestamp() },
    { merge: true },
  )
}

/** Live email templates, merged over the built-in defaults. */
export function subscribeToTemplates(
  onData: (settings: TemplateSettings) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, CRM_COLLECTIONS.settings, SETTINGS_DOCS.templates),
    (snap) => {
      const d = snap.data()
      onData({
        templates: { ...DEFAULT_TEMPLATES, ...(d?.templates ?? {}) },
        updatedAt: d?.updatedAt,
        updatedBy: d?.updatedBy,
      })
    },
    (err) => onError(err as Error),
  )
}

/**
 * Write one template back. Uses a nested merge so two admins editing different
 * stages at the same time do not clobber each other.
 */
export async function saveTemplate(
  key: TemplateKey,
  template: EmailTemplate,
  updatedBy: string,
): Promise<void> {
  await setDoc(
    doc(db, CRM_COLLECTIONS.settings, SETTINGS_DOCS.templates),
    { templates: { [key]: template }, updatedBy, updatedAt: serverTimestamp() },
    { merge: true },
  )
}


/* -------------------------------------------------------------------------- */
/*  Company details                                                            */
/* -------------------------------------------------------------------------- */

const str = (v: unknown) => (typeof v === 'string' ? v : '')

function mapBoard(v: unknown): BoardMember[] {
  if (!Array.isArray(v)) return []
  return v
    .map((m) => ({ name: str((m ?? {}).name), role: str((m ?? {}).role) }))
    .filter((m) => m.name)
}

/**
 * The company's own facts, live.
 *
 * Falls back to empty rather than to invented values: a formal document with a
 * placeholder organisation number in it is worse than one that refuses to
 * generate, and the generator says which field is missing.
 */
export function subscribeToCompany(
  onData: (c: CompanyDetails) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, CRM_COLLECTIONS.settings, SETTINGS_DOCS.company),
    (snap) => {
      const d = snap.data()
      onData({
        legalName: str(d?.legalName),
        orgNumber: str(d?.orgNumber),
        bankAccount: str(d?.bankAccount),
        parValue:
          typeof d?.parValue === 'number' && Number.isFinite(d.parValue) ? d.parValue : null,
        board: mapBoard(d?.board),
      })
    },
    (err) => onError(err as Error),
  )
}

export async function saveCompany(c: CompanyDetails, updatedBy: string): Promise<void> {
  await setDoc(
    doc(db, CRM_COLLECTIONS.settings, SETTINGS_DOCS.company),
    { ...c, updatedBy, updatedAt: serverTimestamp() },
    { merge: true },
  )
}

export { EMPTY_COMPANY }
